import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { CreditCard, MapPin, ShieldCheck, Store, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { couponApi, orderApi, storeApi } from '../services/api';

const fmt = (n) => 'Rs. ' + Number(n || 0).toLocaleString('en-IN');

const loadRazorpaySdk = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout'));
    document.body.appendChild(script);
  });

const CheckoutPage = () => {
  const { user } = useAuth();
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const defaultAddress = user?.addresses?.find((address) => address.isDefault) || user?.addresses?.[0];
  const [address, setAddress] = useState(defaultAddress || { fullAddress: '', city: '', state: '', pincode: '', landmark: '' });
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [fulfillmentType, setFulfillmentType] = useState('delivery');
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [delivery, setDelivery] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [checking, setChecking] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [message, setMessage] = useState('');

  const summary = useMemo(() => {
    const coupon = coupons.find((item) => item.code === selectedCoupon);
    const applicableIds = (coupon?.applicableProducts || []).map((product) => String(product._id || product));
    const applicableSubtotal = coupon && applicableIds.length
      ? items.filter((item) => applicableIds.includes(String(item.id))).reduce((sum, item) => sum + item.price * item.qty, 0)
      : total;
    const minCartValue = coupon?.minCartValue || coupon?.minOrderAmount || 0;
    const canApplyCoupon = coupon && total >= minCartValue && applicableSubtotal > 0;
    const couponValue = coupon?.value || coupon?.discount || 0;
    const discountAmount = canApplyCoupon
      ? coupon.type === 'percentage' || coupon.discountType === 'percentage'
        ? Math.round(applicableSubtotal * (couponValue / 100))
        : Math.min(applicableSubtotal, couponValue)
      : 0;
    const taxableSubtotal = Math.max(0, total - discountAmount);
    const deliveryCharge = fulfillmentType === 'pickup' ? 0 : delivery?.deliverable ? delivery.charge || 0 : total >= 10000 ? 0 : 499;
    const taxAmount = Math.round(taxableSubtotal * 0.18);
    const totalAmount = taxableSubtotal + taxAmount + deliveryCharge;
    const bookingAmount = fulfillmentType === 'pickup' ? Math.min(totalAmount, Math.max(500, Math.round(totalAmount * 0.1))) : totalAmount;
    return { subtotal: total, discountAmount, taxAmount, deliveryCharge, totalAmount, bookingAmount, balanceDue: Math.max(0, totalAmount - bookingAmount) };
  }, [coupons, delivery, fulfillmentType, items, selectedCoupon, total]);

  useEffect(() => {
    storeApi.list()
      .then((response) => {
        const list = response.data?.data || [];
        setStores(list);
        if (list[0]?._id) setSelectedStoreId(list[0]._id);
      })
      .catch(() => setStores([]));
  }, []);

  useEffect(() => {
    couponApi.list()
      .then((response) => setCoupons((response.data?.data || []).filter((coupon) => coupon.status !== 'expired' && coupon.isActive !== false)))
      .catch(() => setCoupons([]));
  }, []);

  useEffect(() => {
    if (fulfillmentType === 'pickup') setPaymentMethod('razorpay');
  }, [fulfillmentType]);

  if (!user) return <Navigate to="/login" replace />;
  if (!items.length) {
    return (
      <main className="bg-[#FAF7F2] min-h-[60vh] py-20 text-center">
        <h1 className="font-serif text-3xl font-bold text-[#1F2A44]">Your cart is empty</h1>
        <Link to="/shop" className="inline-block mt-5 bg-[#E8B530] px-6 py-3 text-[#1F2A44] font-semibold">Shop Now</Link>
      </main>
    );
  }

  const checkPincode = async () => {
    setChecking(true);
    setMessage('');
    try {
      const response = await orderApi.checkDelivery(address.pincode);
      setDelivery(response.data.data);
      if (!response.data.data.deliverable) setMessage('Sorry, delivery is not available for this pincode.');
    } catch (error) {
      setDelivery({ deliverable: false });
      setMessage(error.response?.data?.message || 'Delivery is not available for this pincode.');
    } finally {
      setChecking(false);
    }
  };

  const orderPayload = (paymentInfo) => ({
    items: items.map((item) => ({
      id: item.id,
      productId: item.id,
      variantId: item.selectedVariantId,
      name: item.name,
      sku: item.sku,
      qty: item.qty,
      quantity: item.qty,
      price: item.price,
      marketPrice: item.marketPrice,
      discount: item.discount,
      variantLabel: item.variantLabel,
      gst: item.taxes?.gst || 18
    })),
    shippingAddress: address,
    fulfillment: fulfillmentType === 'pickup' ? { type: 'pickup', storeId: selectedStoreId } : { type: 'delivery' },
    ...(selectedCoupon && { couponCode: selectedCoupon }),
    paymentInfo
  });

  const createBackendOrder = async (paymentInfo) => {
    const response = await orderApi.create(orderPayload(paymentInfo));
    return response.data.data;
  };

  const placeOrder = async () => {
    if (fulfillmentType === 'delivery' && !delivery?.deliverable) {
      setMessage('Please verify a serviceable pincode before placing order.');
      return;
    }
    if (fulfillmentType === 'pickup' && !selectedStoreId) {
      setMessage('Please select a pickup store before placing order.');
      return;
    }
    if (fulfillmentType === 'pickup' && paymentMethod !== 'razorpay') {
      setMessage('Pickup orders require minimum booking payment online.');
      return;
    }

    setPlacing(true);
    setMessage('');

    try {
      if (paymentMethod === 'razorpay') {
        setMessage('Opening Razorpay payment...');
        const payableNow = fulfillmentType === 'pickup' ? summary.bookingAmount : summary.totalAmount;
        const paymentOrderResponse = await orderApi.createPaymentOrder({ amount: payableNow });
        const paymentOrder = paymentOrderResponse.data?.data;
        if (!paymentOrder?.keyId || String(paymentOrder.id || '').startsWith('mock_order_')) {
          const order = await createBackendOrder({
            method: 'razorpay',
            status: 'paid',
            paidAmount: payableNow,
            razorpayOrderId: paymentOrder?.id || `test_order_${Date.now()}`,
            razorpayPaymentId: `test_payment_${Date.now()}`
          });
          clearCart();
          navigate(`/orders/${order._id}`);
          return;
        }

        await loadRazorpaySdk();
        const paymentResult = await new Promise((resolve, reject) => {
          const razorpay = new window.Razorpay({
            key: paymentOrder.keyId,
            amount: paymentOrder.amount,
            currency: paymentOrder.currency || 'INR',
            name: 'FOSEN',
            description: 'Furniture order payment',
            order_id: paymentOrder.id,
            prefill: {
              name: user.name || user.fullName,
              email: user.email,
              contact: user.phone
            },
            handler: resolve,
            modal: {
              ondismiss: () => reject(new Error('Payment cancelled by customer'))
            }
          });
          razorpay.on('payment.failed', (response) => reject(new Error(response.error?.description || 'Payment failed')));
          razorpay.open();
        });

        await orderApi.verifyPayment({
          orderId: paymentResult.razorpay_order_id,
          paymentId: paymentResult.razorpay_payment_id,
          signature: paymentResult.razorpay_signature
        });

        const order = await createBackendOrder({
          method: 'razorpay',
          status: 'paid',
          paidAmount: payableNow,
          razorpayOrderId: paymentResult.razorpay_order_id,
          razorpayPaymentId: paymentResult.razorpay_payment_id,
          razorpaySignature: paymentResult.razorpay_signature
        });
        clearCart();
        navigate(`/orders/${order._id}`);
        return;
      }

      const order = await createBackendOrder({ method: 'cod', status: 'pending', paidAmount: 0 });
      clearCart();
      navigate(`/orders/${order._id}`);
    } catch (error) {
      setMessage(error.response?.data?.message || error.message || 'Order could not be placed. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <main className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#1F2A44]">Checkout</h1>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-20 grid lg:grid-cols-[1fr_360px] gap-8">
        <section className="space-y-5">
          <div className="bg-white border border-[#1F2A44]/10 p-5">
            <div className="flex items-center gap-2 mb-4"><MapPin className="text-[#E8B530]" /><h2 className="font-serif text-xl font-bold text-[#1F2A44]">Fulfillment</h2></div>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[{ id: 'delivery', label: 'Home Delivery' }, { id: 'pickup', label: 'Pick up from Store' }].map((option) => (
                <label key={option.id} className={`border p-4 cursor-pointer text-sm flex items-center gap-2 ${fulfillmentType === option.id ? 'border-[#E8B530] bg-[#E8B530]/10' : 'border-[#1F2A44]/10'}`}>
                  <input type="radio" className="accent-[#E8B530]" checked={fulfillmentType === option.id} onChange={() => setFulfillmentType(option.id)} />
                  {option.label}
                </label>
              ))}
            </div>

            {fulfillmentType === 'delivery' ? (
              <>
                <div className="grid md:grid-cols-2 gap-3">
                  <input value={address.fullAddress} onChange={(e) => setAddress({ ...address, fullAddress: e.target.value })} placeholder="Full address" className="md:col-span-2 border border-[#1F2A44]/20 px-4 py-3 text-sm" />
                  <input value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} placeholder="City" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
                  <input value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} placeholder="State" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
                  <input value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} placeholder="Pincode" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
                  <input value={address.landmark || ''} onChange={(e) => setAddress({ ...address, landmark: e.target.value })} placeholder="Landmark" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
                </div>
                <button onClick={checkPincode} disabled={!address.pincode || checking} className="mt-4 bg-[#1F2A44] text-[#FAF7F2] px-5 py-3 text-sm font-semibold uppercase tracking-wider disabled:opacity-60">
                  {checking ? 'Checking...' : 'Check Delivery'}
                </button>
                {delivery?.deliverable && <p className="mt-3 text-sm text-green-700">Delivery available in {delivery.city}, {delivery.state}. Charge: {fmt(delivery.charge)}. Estimated {delivery.estimatedDays} days.</p>}
              </>
            ) : (
              <div>
                <select value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)} className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm">
                  {stores.length === 0 && <option value="">No active stores available</option>}
                  {stores.map((store) => <option key={store._id} value={store._id}>{store.name} - {store.address?.city}</option>)}
                </select>
                {stores.find((store) => store._id === selectedStoreId) && (
                  <div className="mt-3 border border-[#1F2A44]/10 p-4 text-sm text-[#1F2A44]/70">
                    <div className="flex items-center gap-2 text-[#1F2A44] font-semibold"><Store size={16} /> {stores.find((store) => store._id === selectedStoreId)?.name}</div>
                    <p>{stores.find((store) => store._id === selectedStoreId)?.address?.fullAddress}</p>
                    <p>{stores.find((store) => store._id === selectedStoreId)?.address?.city}, {stores.find((store) => store._id === selectedStoreId)?.address?.state} - {stores.find((store) => store._id === selectedStoreId)?.address?.pincode}</p>
                    <p>{stores.find((store) => store._id === selectedStoreId)?.openingHours}</p>
                  </div>
                )}
              </div>
            )}
            {message && <p className="mt-3 text-sm text-red-600">{message}</p>}
          </div>

          <div className="bg-white border border-[#1F2A44]/10 p-5">
            <h2 className="font-serif text-xl font-bold text-[#1F2A44] mb-4">Coupon</h2>
            <select value={selectedCoupon} onChange={(e) => setSelectedCoupon(e.target.value)} className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm">
              <option value="">No coupon</option>
              {coupons.map((coupon) => (
                <option key={coupon._id} value={coupon.code}>
                  {coupon.code} - {coupon.type === 'percentage' || coupon.discountType === 'percentage' ? `${coupon.value || coupon.discount}%` : fmt(coupon.value || coupon.discount)} off
                </option>
              ))}
            </select>
            {selectedCoupon && summary.discountAmount <= 0 && <p className="mt-2 text-sm text-red-600">This coupon is not applicable to the current cart.</p>}
            {selectedCoupon && summary.discountAmount > 0 && <p className="mt-2 text-sm text-green-700">Coupon applied: -{fmt(summary.discountAmount)}</p>}
          </div>

          <div className="bg-white border border-[#1F2A44]/10 p-5">
            <div className="flex items-center gap-2 mb-4"><CreditCard className="text-[#E8B530]" /><h2 className="font-serif text-xl font-bold text-[#1F2A44]">Payment Method</h2></div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[{ id: 'cod', label: 'Cash on Delivery' }, { id: 'razorpay', label: fulfillmentType === 'pickup' ? 'Booking Payment / UPI / Card' : 'Razorpay / UPI / Card' }].map((method) => (
                <label key={method.id} className={`border p-4 text-sm ${fulfillmentType === 'pickup' && method.id === 'cod' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${paymentMethod === method.id ? 'border-[#E8B530] bg-[#E8B530]/10' : 'border-[#1F2A44]/10'}`}>
                  <input type="radio" disabled={fulfillmentType === 'pickup' && method.id === 'cod'} className="accent-[#E8B530] mr-2" checked={paymentMethod === method.id} onChange={() => setPaymentMethod(method.id)} />
                  {method.label}
                </label>
              ))}
            </div>
          </div>
        </section>

        <aside className="bg-white border border-[#1F2A44]/10 p-6 h-fit lg:sticky lg:top-32">
          <div className="flex items-center gap-2"><Truck className="text-[#E8B530]" /><h2 className="font-serif text-xl font-bold text-[#1F2A44]">Order Summary</h2></div>
          <div className="mt-5 space-y-3 text-sm">
            {items.map((item) => (
              <div key={item.cartKey || item.id} className="flex justify-between gap-3">
                <span>
                  {item.name} x {item.qty}
                  {item.variantLabel && <span className="block text-xs text-[#1F2A44]/60">{item.variantLabel}</span>}
                </span>
                <span>{fmt(item.price * item.qty)}</span>
              </div>
            ))}
            <div className="border-t pt-3 flex justify-between"><span>Subtotal</span><strong>{fmt(summary.subtotal)}</strong></div>
            {summary.discountAmount > 0 && <div className="flex justify-between text-green-700"><span>Coupon</span><strong>-{fmt(summary.discountAmount)}</strong></div>}
            <div className="flex justify-between"><span>GST</span><strong>{fmt(summary.taxAmount)}</strong></div>
            <div className="flex justify-between"><span>{fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'}</span><strong>{fmt(summary.deliveryCharge)}</strong></div>
            <div className="border-t pt-3 flex justify-between text-lg"><span>Total</span><strong>{fmt(summary.totalAmount)}</strong></div>
            {fulfillmentType === 'pickup' && (
              <>
                <div className="flex justify-between text-green-700"><span>Booking now</span><strong>{fmt(summary.bookingAmount)}</strong></div>
                <div className="flex justify-between text-amber-700"><span>Pay at store</span><strong>{fmt(summary.balanceDue)}</strong></div>
              </>
            )}
          </div>
          <button onClick={placeOrder} disabled={placing} className="w-full mt-6 bg-[#E8B530] text-[#1F2A44] py-3.5 font-semibold uppercase tracking-wider text-sm disabled:opacity-60">
            {placing ? 'Processing...' : 'Place Order'}
          </button>
          <div className="flex items-center gap-2 mt-4 text-xs text-[#1F2A44]/60"><ShieldCheck size={14} /> Secure checkout with verified fulfillment details</div>
        </aside>
      </div>
    </main>
  );
};

export default CheckoutPage;
